import { useCallback, useEffect, useState, useSyncExternalStore } from "react";
import { useLocation } from "react-router-dom";
import {
  clearDebugEntries,
  clearSiteCache,
  getDebugSnapshot,
  installDebugCapture,
  subscribeDebug,
  type DebugEntry,
} from "@/lib/debugLog";

const KIND_STYLE: Record<DebugEntry["kind"], { icon: string; tone: string; label: string }> = {
  error: { icon: "ri-error-warning-line", tone: "text-accent-700 bg-accent-100", label: "Erro" },
  request: { icon: "ri-cloud-off-line", tone: "text-secondary-900 bg-secondary-100", label: "Conexão" },
  info: { icon: "ri-information-line", tone: "text-primary-700 bg-primary-100", label: "Info" },
};

function formatTime(at: number): string {
  return new Date(at).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
}

// Painel flutuante de diagnóstico: mostra os erros de JavaScript, as
// requisições que falharam e a rota atual. Disponível no site E no painel.
export default function DebugPanel() {
  const entries = useSyncExternalStore(subscribeDebug, getDebugSnapshot, getDebugSnapshot);
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const isAdmin = location.pathname.startsWith("/admin");
  const problemCount = entries.filter((item) => item.kind !== "info").length;

  useEffect(() => {
    if (!isAdmin) return;
    installDebugCapture();
  }, [isAdmin]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.shiftKey && event.key.toLowerCase() === "d") {
        event.preventDefault();
        setOpen((prev) => !prev);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const handleClearCache = useCallback(async () => {
    setClearing(true);
    setNotice(null);
    try {
      const cleared = await clearSiteCache();
      setNotice(cleared.length ? `Cache limpo: ${cleared.join(", ")}.` : "Nada de cache para limpar — recarregue abaixo.");
    } catch {
      setNotice("Não foi possível limpar tudo, mas o essencial foi feito.");
    } finally {
      setClearing(false);
    }
  }, []);

  // O painel de debug fica disponível apenas no admin, nunca no site público.
  if (!isAdmin) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="fixed bottom-4 z-[70] left-4 lg:left-[280px] inline-flex items-center gap-2 h-10 pl-3 pr-3.5 rounded-full border border-background-200 bg-background-50 text-foreground-800 text-xs font-semibold hover:border-primary-300 hover:text-primary-600 transition-colors"
        aria-label="Abrir painel de Debug"
        title="Debug — erros e cache (Ctrl+Shift+D)"
      >
        <span className="relative flex items-center justify-center w-5 h-5">
          <i className="ri-bug-line text-lg" />
          {problemCount > 0 && (
            <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-accent-500 text-foreground-950 text-[10px] font-bold flex items-center justify-center">
              {problemCount > 9 ? "9+" : problemCount}
            </span>
          )}
        </span>
        Debug
      </button>

      {open && (
        <div className="fixed bottom-16 z-[70] left-4 lg:left-[280px] w-[min(24rem,calc(100vw-2rem))] rounded-lg border border-background-200 bg-background-50 flex flex-col max-h-[70vh]">
          <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-background-200/70">
            <div className="min-w-0">
              <div className="font-heading text-sm font-bold text-foreground-900">Painel de Debug</div>
              <div className="text-[11px] text-foreground-500 truncate font-mono">
                {location.pathname}
                {location.search}
                {location.hash}
              </div>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="p-1.5 rounded-md text-foreground-500 hover:bg-background-100 hover:text-foreground-800 transition-colors"
              aria-label="Fechar painel de Debug"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-close-line" />
              </span>
            </button>
          </div>

          <div className="px-4 py-3 border-b border-background-200/70 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={handleClearCache}
              disabled={clearing}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-primary-500 text-background-50 text-xs font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap disabled:opacity-60"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className={clearing ? "ri-loader-4-line animate-spin" : "ri-eraser-line"} />
              </span>
              {clearing ? "Limpando…" : "Limpar cache"}
            </button>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-background-300/60 text-foreground-800 text-xs font-semibold hover:border-primary-300 hover:text-primary-600 transition-colors whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-refresh-line" />
              </span>
              Recarregar
            </button>
            <button
              type="button"
              onClick={() => clearDebugEntries()}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-foreground-600 text-xs font-medium hover:bg-background-100 transition-colors whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-delete-bin-6-line" />
              </span>
              Limpar registros
            </button>
          </div>

          {notice && (
            <div className="px-4 py-2.5 text-xs text-primary-800 bg-primary-100/70 border-b border-background-200/70">
              {notice}
            </div>
          )}

          <div className="flex-1 overflow-y-auto">
            {entries.length === 0 ? (
              <div className="px-4 py-10 text-center">
                <div className="w-10 h-10 mx-auto rounded-full bg-primary-100 text-primary-600 flex items-center justify-center mb-3">
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-checkbox-circle-line text-lg" />
                  </span>
                </div>
                <p className="text-sm text-foreground-600">
                  Nenhum erro registrado até agora. Navegue pelo site que este painel avisa se algo quebrar.
                </p>
              </div>
            ) : (
              <ul className="divide-y divide-background-200/60">
                {entries.map((entry) => {
                  const style = KIND_STYLE[entry.kind];
                  return (
                    <li key={entry.id} className="px-4 py-3">
                      <div className="flex items-start gap-3">
                        <span className={`w-7 h-7 shrink-0 rounded-md flex items-center justify-center ${style.tone}`}>
                          <span className="w-4 h-4 flex items-center justify-center">
                            <i className={style.icon} />
                          </span>
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5">
                            <span className="text-[10px] font-semibold uppercase tracking-wide text-foreground-500">
                              {style.label}
                            </span>
                            <span className="text-[10px] text-foreground-400">{formatTime(entry.at)}</span>
                          </div>
                          <p className="text-xs text-foreground-900 break-words">{entry.message}</p>
                          {entry.detail && (
                            <pre className="mt-1.5 text-[10px] leading-relaxed text-foreground-500 font-mono whitespace-pre-wrap break-words max-h-32 overflow-y-auto">
                              {entry.detail}
                            </pre>
                          )}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div className="px-4 py-2.5 border-t border-background-200/70 text-[11px] text-foreground-500">
            Atalho: <span className="font-mono">Ctrl</span> + <span className="font-mono">Shift</span> +{" "}
            <span className="font-mono">D</span>
          </div>
        </div>
      )}
    </>
  );
}